import { useWeeks } from '@/lib/queries';
import type { Week, WeeklyExecutiveNote } from '@/lib/types';
import { findCurrentWeek } from '@/lib/utils/week';
import { useWeeklyExecutiveNotes } from '@/pages/meetings/queries';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 주간회의 대시보드의 대표님 전달/지시사항을 홈에 올린다.
 *
 * 이번 주차에 적힌 내용이 없으면 가장 최근에 적힌 것을 대신 보여준다 — 대표님 지시는 한 주만
 * 유효한 게 아니라 새 지시가 나올 때까지 유효하기 때문이다. 판정 규칙은 대시보드({@link '/dashboard'})의
 * `resolveNote` 와 같다.
 */

type ResolvedNote = { note: WeeklyExecutiveNote; sourceWeek: Week; isFallback: boolean } | null;

function resolveNote(
  notes: WeeklyExecutiveNote[],
  weeks: Week[],
  week: Week,
  noteType: 'hq' | 'site',
): ResolvedNote {
  const currentIndex = weeks.findIndex((candidate) => candidate.id === week.id);
  if (currentIndex < 0) return null;

  const direct = notes.find((note) => note.noteType === noteType && note.weekId === week.id);
  if (direct) return { note: direct, sourceWeek: week, isFallback: false };

  // 목록은 최근 순이므로 뒤로 갈수록 과거다.
  for (let index = currentIndex + 1; index < weeks.length; index += 1) {
    const olderWeek = weeks[index] as Week;
    const older = notes.find((note) => note.noteType === noteType && note.weekId === olderWeek.id);
    if (older) return { note: older, sourceWeek: olderWeek, isFallback: true };
  }

  return null;
}

export function WeeklyReportWidget() {
  const weeks = useWeeks();
  const notes = useWeeklyExecutiveNotes();

  const currentWeek = findCurrentWeek(weeks.data ?? []);
  const hq = currentWeek ? resolveNote(notes.data ?? [], weeks.data ?? [], currentWeek, 'hq') : null;
  const site = currentWeek ? resolveNote(notes.data ?? [], weeks.data ?? [], currentWeek, 'site') : null;

  return (
    <WidgetFrame
      title="대표님 전달/지시사항"
      icon="clipboard"
      to="/dashboard"
      pending={weeks.isPending || notes.isPending}
      error={weeks.error ?? notes.error}
      empty={!currentWeek || (!hq && !site)}
      emptyText="이번 주 전달된 지시사항이 없습니다."
      headerRight={currentWeek ? <span className="badge badge-gray">{currentWeek.label}</span> : undefined}
    >
      <div className="space-y-2">
        <NoteBlock label="본사" labelColor="#1d4ed8" resolved={hq} />
        <NoteBlock label="현장" labelColor="#b45309" resolved={site} />
      </div>
    </WidgetFrame>
  );
}

function NoteBlock({ label, labelColor, resolved }: { label: string; labelColor: string; resolved: ResolvedNote }) {
  const content = resolved?.note.content.trim();

  return (
    <div className="rounded-lg px-3 py-2" style={{ background: 'var(--bg-hover)' }}>
      <div className="flex items-center gap-1.5">
        <span className="font-bold text-ui-xs" style={{ color: labelColor }}>
          {label}
        </span>
        {resolved?.isFallback && (
          <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>· {resolved.sourceWeek.label}</span>
        )}
      </div>
      <p
        className="mt-1 text-ui-sm"
        style={{
          lineHeight: 1.6,
          color: content ? 'var(--text-primary)' : 'var(--text-muted)',
          whiteSpace: 'pre-wrap',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {content || '입력된 전달사항이 없습니다.'}
      </p>
    </div>
  );
}
